import type { ReactNode } from "react";

export type Column<T> = {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
  /** Phones: "title" becomes the card heading, "hide" drops the cell. */
  mobile?: "title" | "hide";
  align?: "right";
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  rowLabel?: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  caption: string;
  className?: string;
  /** Pager etc., inside the table frame. */
  footer?: ReactNode;
  /** A summary row (e.g. totals), keyed by column key. */
  footRow?: Record<string, ReactNode>;
};

/** A table on wide screens; one card per row on phones (like the pricing table on the site). */
export function DataTable<T>({ columns, rows, rowKey, onRowClick, rowLabel, loading, empty, caption, className = "", footer, footRow }: Props<T>) {
  const cls = (c: Column<T>) =>
    [c.className, c.align === "right" ? "num" : "", c.mobile === "title" ? "m-title" : "", c.mobile === "hide" ? "m-hide" : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className={`table-wrap dt-wrap ${className}`}>
      <table className="dt">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={cls(c)}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody aria-busy={loading || undefined}>
          {loading && !rows.length
            ? Array.from({ length: 6 }, (_, i) => (
                <tr key={`sk${i}`} className="sk-row" aria-hidden="true">
                  {columns.map((c) => (
                    <td key={c.key} className={cls(c)}><span className="sk" /></td>
                  ))}
                </tr>
              ))
            : rows.map((r) => (
                <tr
                  key={rowKey(r)}
                  className={onRowClick ? "clickable" : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  aria-label={onRowClick && rowLabel ? rowLabel(r) : undefined}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
                            e.preventDefault();
                            onRowClick(r);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((c) => (
                    <td key={c.key} className={cls(c)} data-label={c.header}>{c.cell(r)}</td>
                  ))}
                </tr>
              ))}
        </tbody>
        {footRow && rows.length > 0 && (
          <tfoot>
            <tr>
              {columns.map((c) => (
                <td key={c.key} className={cls(c)} data-label={footRow[c.key] !== undefined && footRow[c.key] !== null ? c.header : undefined}>{footRow[c.key]}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      {!loading && !rows.length && empty && <div className="dt-empty">{empty}</div>}
      {footer && <div className="dt-foot">{footer}</div>}
    </div>
  );
}
